import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import type { GenericTableColumn } from "./GenericTable";
import { getColumnId, getColumnLabel } from "./useColumnVisibility";

interface Props<T> {
  columns: GenericTableColumn<T>[];
  isVisible: (id: string) => boolean;
  toggle: (id: string) => void;
  requiredIds?: string[];
  title?: string;
  subtitle?: string;
}

const POPOVER_WIDTH = 320;
const POPOVER_MAX_HEIGHT = 420;
const GAP = 8;

export function ColumnVisibilityControl<T>({
  columns,
  isVisible,
  toggle,
  requiredIds = [],
  title = "Attribute columns",
  subtitle = "Select visible attribute columns",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const computePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.right - POPOVER_WIDTH;
    if (left < 8) left = 8;
    if (left + POPOVER_WIDTH > vw - 8) left = vw - POPOVER_WIDTH - 8;
    let top = rect.bottom + GAP;
    if (top + POPOVER_MAX_HEIGHT > vh - 8) {
      const above = rect.top - GAP - POPOVER_MAX_HEIGHT;
      top = above > 8 ? above : Math.max(8, vh - POPOVER_MAX_HEIGHT - 8);
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (open) computePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => computePosition();
    const onScroll = () => computePosition();
    const onDoc = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return columns;
    return columns.filter((c) => getColumnLabel(c).toLowerCase().includes(q));
  }, [columns, filter]);

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: POPOVER_WIDTH,
              maxHeight: POPOVER_MAX_HEIGHT,
              zIndex: 10000,
            }}
            className="bg-semantic-bg-primary border border-semantic-border-secondary rounded shadow-lg p-4 flex flex-col"
          >
            <div className="text-sm font-semibold text-semantic-text-primary">
              {title}
            </div>
            <div className="text-xs text-semantic-text-secondary mb-3">
              {subtitle}
            </div>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter columns"
              className="w-full border border-semantic-border-secondary rounded px-2 py-1 mb-3 bg-transparent text-sm text-semantic-text-primary placeholder:text-semantic-text-secondary focus:outline-none focus:border-semantic-border-brand"
            />
            <div className="overflow-y-auto flex flex-col flex-1 min-h-0">
              {filtered.length === 0 && (
                <div className="text-xs text-semantic-text-secondary py-2">
                  No matches
                </div>
              )}
              {filtered.map((col) => {
                const id = getColumnId(col);
                const label = getColumnLabel(col);
                const required = requiredIds.includes(id);
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between py-2 border-b border-semantic-border-secondary last:border-b-0"
                  >
                    <span className="text-sm text-semantic-text-primary">
                      {label}
                      {required && (
                        <span className="ml-2 text-xs text-semantic-text-secondary">
                          (required)
                        </span>
                      )}
                    </span>
                    <GenericSwitch
                      checked={isVisible(id)}
                      onChange={() => toggle(id)}
                      disabled={required}
                    />
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-10 h-10 border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active"
        aria-label="Show or hide columns"
        title="Show or hide columns"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      {popover}
    </>
  );
}

export default ColumnVisibilityControl;
